import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./PutAwayPage.scss";
import * as putAwayService from "../../Services/Http/PutAwayService";
import { toast } from "react-toastify";
import { BlockerFunction, useBlocker, useNavigate } from "react-router";
import { dataContext } from "../../Context/contextProvider";
import { PutAwayScanResponse } from "../../Models/PutAwayScanResponse";
import { Button, Modal } from "react-bootstrap";
import { TransHeaderResponse } from "../../Models/TransHeaderResponse";
import { PutAwayListResponse } from "../../Models/PutAwayListResponse";
import { BooleanResponse } from "../../Models/BooleanResponse";

interface Props {}

const successSound = new Audio();
successSound.src = "/assets/sounds/success.mp3";
successSound.load();

const errorSound = new Audio();
errorSound.src = "/assets/sounds/error.mp3";
errorSound.load();

const questionSound = new Audio();
questionSound.src = "/assets/sounds/question.mp3";
questionSound.load();

const PutAwayPage = (props: Props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const searchBoxInputRef = useRef<HTMLInputElement>(null);
  const [currentData, setCurrentData] = useState<PutAwayListResponse | null>(null);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const dataStore = useContext(dataContext);

  useLayoutEffect(() => {
    if (searchBoxInputRef.current) {
      searchBoxInputRef.current.focus();
    }
  }, []);

  const refreshItems = useCallback(async (): Promise<void> => {
    try {
      setSubmitDisabled(true);
      const data: PutAwayListResponse = await putAwayService.getPutAwayListAPI(dataStore.serverUrl || "", dataStore.dbKey || "");
      setCurrentData(data);
    } catch (error: any) {
      setCurrentData(null);
      toast.error(error.message || error, { autoClose: false });
    } finally {
      setSubmitDisabled(false);
    }
  }, [dataStore.dbKey, dataStore.serverUrl]);

  useEffect(() => {
    refreshItems();
  }, [refreshItems]);

  const doScan = useCallback(
    (barcode: string): void => {
      if (!barcode || barcode.trim() === "") {
        setSearchTerm("");
        errorSound.play();
        toast.error("Barcode must have a value", { autoClose: 1000 });
        return;
      }

      putAwayService
        .scanPutAwayItemAPI(barcode.trim(), dataStore.serverUrl || "", dataStore.dbKey || "", true, false, true, undefined, dataStore.location ?? undefined)
        .then((scanResponse: PutAwayScanResponse) => {
          if (!scanResponse || scanResponse.hasError) {
            setSearchTerm("");
            errorSound.play();
            const errMsg = scanResponse && scanResponse.errorMessage ? scanResponse.errorMessage : "Bin not found";
            toast.error(errMsg, { autoClose: 1000 });
            return;
          }
          setSearchTerm("");
          successSound.play();
          const successMsg = scanResponse && scanResponse.successMessage ? scanResponse.successMessage : "Scanned successfully";
          toast.success(successMsg, { autoClose: 1000 });
          return refreshItems();
        })
        .catch((error: any) => {
          toast.error(error.message || error, { autoClose: false });
        });
    },
    [dataStore.dbKey, dataStore.serverUrl, dataStore.location, refreshItems],
  );

  const onBarcodeScanned = useCallback(
    (barcode: string): void => {
      doScan(barcode);
    },
    [doScan],
  );

  const onKeyPressed = useCallback(
    (event: any) => {
      // if (event.target && event.target instanceof HTMLInputElement &&
      //     event.target.name === searchBoxInputRef.current!.name) {
      //         return;
      // }
      if (event.target && event.target instanceof HTMLInputElement) {
        return;
      }
      const keyPressed = event.key ? event.key : "";
      if (keyPressed === "") {
        return;
      }
      if (keyPressed.toLowerCase() === "tab" || keyPressed.toLowerCase() === "enter") {
        event.preventDefault();
        setSearchTerm((oldState) => {
          const newState = oldState;
          onBarcodeScanned(newState);
          return newState;
        });
      } else if (keyPressed.length === 1) {
        setSearchTerm((oldState) => {
          const newState = oldState + keyPressed;
          return newState;
        });
      }
    },
    [onBarcodeScanned],
  );

  useEffect(() => {
    window.addEventListener("keydown", onKeyPressed);
    return () => {
      window.removeEventListener("keydown", onKeyPressed);
    };
  }, [onKeyPressed]);

  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      const hasUnsavedChanges = currentData?.rows && currentData.rows.length > 0;
      if (!hasUnsavedChanges) {
        return;
      } else if (!window.confirm("Current order has not been fully scanned. Leave page ?")) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, [currentData]);

  // useKeyDown((event: any) => {
  //     onKeyPressed(event);
  // }, []);

  let shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => !!currentData?.rows && currentData.rows.length > 0 && currentLocation.pathname !== nextLocation.pathname,
    [currentData],
  );
  let blocker = useBlocker(shouldBlock);

  // Reset the blocker if the user cleans the form
  useEffect(() => {
    if (blocker.state === "blocked" && (!currentData?.rows || currentData.rows.length === 0)) {
      blocker.reset();
    } else if (blocker.state === "blocked" && !!currentData?.rows && currentData.rows.length > 0) {
      //show modal depending on blocker status
      setModalOpen(true);
    } else if (blocker.state !== "blocked") {
      setModalOpen(false);
    }
  }, [blocker, currentData]);

  const submitPutAway = useCallback(async (): Promise<void> => {
    if (!currentData?.rows || currentData.rows.length === 0) {
      toast.error("List is empty and was not submitted", { autoClose: 1000 });
      setSubmitDisabled(false);
      return;
    }
    try {
      const putAwayResponse: TransHeaderResponse = await putAwayService.submitPutAwayListAPI(dataStore.serverUrl || "", dataStore.dbKey || "");
      if (putAwayResponse) {
        if (putAwayResponse.errorMessage) {
          toast.error(putAwayResponse.errorMessage, { autoClose: false });
        } else if (putAwayResponse.transRefCode) {
          toast.success(`Submitted successfully, TransRefCode: ${putAwayResponse.transRefCode}`, { autoClose: 2000 });
          setSubmitDisabled(false);
          setCurrentData(null);
          navigate("/menu");
        }
      } else {
        toast.error("There was an error submitting", { autoClose: false });
        setSubmitDisabled(false);
      }
    } catch (error: any) {
      toast.error(error.message || error, { autoClose: false });
      setSubmitDisabled(false);
    }
  }, [dataStore.dbKey, dataStore.serverUrl, currentData, navigate]);

  const cancelPutAway = useCallback(async (): Promise<void> => {
    try {
      setSubmitDisabled(true);
      const resp: BooleanResponse = await putAwayService.deletePutAwayListAPI(dataStore.serverUrl || "", dataStore.dbKey || "");
      if (resp) {
        if (resp.hasError) {
          toast.error(resp.errorMessage, { autoClose: false });
        } else if (resp.isTrue) {
          toast.success("Cancelled", { autoClose: 2000 });
        } else if (!resp.isTrue) {
          toast.error("There was an error cancelling", { autoClose: false });
        }
        await refreshItems();
        setSubmitDisabled(false);
        return navigate("/menu");
      }
    } catch (error: any) {
      toast.error(error.message || error, { autoClose: false });
      setSubmitDisabled(false);
    }
  }, [dataStore.serverUrl, dataStore.dbKey, refreshItems, navigate]);

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
  };

  const handleKeyUp = (event: any) => {
    if (event.key && event.key.toLowerCase() === "enter") {
      event.preventDefault();
      onSearchClicked();
    }
  };
  const handleDeleteItem = (stockNo: number) => {
    if (!currentData?.rows || currentData.rows.length === 0 || stockNo === 0) {
      return;
    }
    // find the item to delete
    const itemToDelete = currentData.rows.find((item) => item.stockNo === stockNo);
    if (!itemToDelete) {
      return;
    }
    //show confirmation dialog
    if (!window.confirm(`Are you sure you want to delete ${itemToDelete.stockDesc} [${itemToDelete.stockNo}] from the list?`)) {
      return;
    }
    //call delete api
    putAwayService
      .deletePutAwayItemAPI(stockNo, dataStore.serverUrl || "", dataStore.dbKey || "")
      .then((resp: BooleanResponse) => {
        if (resp) {
          if (resp.hasError) {
            toast.error(resp.errorMessage, { autoClose: false });
          } else if (resp.isTrue) {
            toast.success("Item deleted", { autoClose: 2000 });
            refreshItems();
          } else if (!resp.isTrue) {
            toast.error("There was an error deleting the item", { autoClose: false });
            refreshItems();
          }
        }
      })
      .catch((error: any) => {
        toast.error(error.message || error, { autoClose: false });
      });
  };

  const onSearchClicked = () => {
    onBarcodeScanned(searchTerm);
  };

  const onSubmit = () => {
    setSubmitDisabled(true);
    submitPutAway();
    setTimeout(() => {
      setSubmitDisabled(false);
    }, 3000);
  };

  const onCancel = async () => {
    await cancelPutAway();
  };
  const onCloseModal = () => {
    setModalOpen(false);
  };

  let submitButtonCssClass = "btn btn-lg btn-success btn-block fill-half";
  if (!currentData?.rows || currentData.rows.length === 0) {
    submitButtonCssClass += " disabled";
  }
  let cancelButtonCssClass = "btn btn-lg btn-secondary btn-block fill-half";
  if (!currentData?.lastBinCode && (!currentData?.rows || currentData.rows.length === 0)) {
    cancelButtonCssClass += " disabled";
  }
  return (
    <>
      <div className="container-limited-width">
        <div className="row">
          <div className="col-12 p-2">
            <div className="input-group">
              <input
                name="searchBox"
                type="text"
                className="form-control search-box"
                placeholder="Scan barcode"
                autoCapitalize="none"
                aria-describedby="btnGroupAddon"
                onKeyUp={handleKeyUp}
                onChange={handleSearchChange}
                value={searchTerm}
                ref={searchBoxInputRef}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-2 pl-1 pr-1">
            <ul className="list-group">
              <li className="list-group-item d-flex flex-row justify-content-center align-items-center bg-info">
                Put to bin:&nbsp;<span className="fw-bold">{currentData?.lastBinCode || ""}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-2 pl-1 pr-1">
            <ul className="list-group">
              {currentData?.rows &&
                currentData.rows.map((product, index) => {
                  return (
                    <li key={`${product.stockNo}_${index}`} className="list-group-item d-flex flex-row flex-grow-1 align-self-stretch wide-tile">
                      <div className="d-flex flex-column justify-content-between align-items-start flex-grow-1 align-self-stretch">
                        <div className="d-flex fw-bold flex-row">
                          {product.stockDesc} [{product.stockNo}]
                        </div>
                        <div className="d-flex flex-row justify-content-between flex-grow-1 align-self-stretch">
                          <div className="d-flex fill-half">StockCode: {product.stockCode}</div>
                          <div className="d-flex fill-half justify-content-end">From: {product.fromBinCode}</div>
                        </div>
                        <div className="d-flex flex-row justify-content-between flex-grow-1 align-self-stretch">
                          <div className="d-flex justify-content-start">Serial: {product.serialNo}</div>
                          <div className="d-flex justify-content-center">Qty: {product.qty || 0}</div>
                          <div className="d-flex justify-content-end">To: {product.binCode}</div>
                        </div>
                      </div>
                      <div className="d-flex flex-column align-items-center justify-content-center">
                        <Button
                          variant="link"
                          className="text-danger"
                          onClick={() => {
                            handleDeleteItem(product.stockNo || 0);
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </Button>
                      </div>
                    </li>
                  );
                })}
            </ul>
            {submitDisabled && <p className="text-center">Please wait...</p>}
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-2 mt-2 pl-1 pr-1">
            <div className="d-flex gap-2 justify-space-between">
              <button disabled={submitDisabled || !currentData?.rows || currentData.rows.length === 0} className={submitButtonCssClass} type="button" onClick={onSubmit}>
                {submitDisabled ? "Please wait..." : "Submit"}
              </button>
              <button className={cancelButtonCssClass} type="button" onClick={onCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <Modal show={modalOpen} onHide={onCloseModal} backdrop="static" keyboard={false} aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Header>
          <Modal.Title>Items were scanned but not submitted !</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you would like to leave without submitting ?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => blocker.proceed?.()}>
            Leave
          </Button>
          <Button variant="success" onClick={() => blocker.reset?.()}>
            Stay
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PutAwayPage;
